import { FC, useCallback, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { Menu } from '@headlessui/react';
import { DotsHorizontalIcon, PlusIcon, SearchIcon } from '@heroicons/react/outline';
import {
  giftDeliveryCategoryListDataAtom,
  giftDeliveryCategoryListStatusAtom,
} from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.atom';
import { deleteGiftDeliveryCategory } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.controller';
import { useGiftDeliveryCategoryListHook } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.hook';
import { GiftDeliveryCategoryData } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.interface';
import {
  giftItemInfoBrandFilterAtom,
  giftItemInfoDeliveryCategoryFilterAtom,
  giftItemInfoSearchQueryAtom,
  giftItemInfoStatusFilterAtom,
  giftItemInfoSubCategoryFilterAtom,
  giftItemInfoTaxFilterAtom,
  giftItemInfoTypeFilterAtom,
} from '@shared/api/giftItemInfo/giftItemInfo.atom';
import { Loading } from '@shared/components/Loading';
import { invalidateQueriesByUrlPart } from '@shared/hooks/recoil-query';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';

import styles from '../components/GiftBrandManagement.module.css';
import { useSessionPersistedState } from '../hooks/useSessionPersistedState';

type StatusFilter = 'all' | 'enabled' | 'disabled';
const primaryButton =
  'inline-flex items-center justify-center gap-2 rounded bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700';
const secondaryButton =
  'rounded border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50';
const cell = 'block px-5 py-2 xl:table-cell xl:py-5 xl:align-top';

export const GiftDeliveryCategoryListPage: FC = function GiftDeliveryCategoryListPage() {
  const { sellerRole } = useAuth();
  useGiftDeliveryCategoryListHook(sellerRole());
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const status = useRecoilValue(giftDeliveryCategoryListStatusAtom);
  const data = useRecoilValue(giftDeliveryCategoryListDataAtom);
  const setData = useSetRecoilState(giftDeliveryCategoryListDataAtom);
  const [searchQuery, setSearchQuery] = useSessionPersistedState(
    'giftDeliveryCategoryList.search',
    '',
  );
  const [statusFilter, setStatusFilter] = useSessionPersistedState<StatusFilter>(
    'giftDeliveryCategoryList.status',
    'all',
  );
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const setBrand = useSetRecoilState(giftItemInfoBrandFilterAtom);
  const setCategory = useSetRecoilState(giftItemInfoDeliveryCategoryFilterAtom);
  const setType = useSetRecoilState(giftItemInfoTypeFilterAtom);
  const setSearch = useSetRecoilState(giftItemInfoSearchQueryAtom);
  const setStatus = useSetRecoilState(giftItemInfoStatusFilterAtom);
  const setSubCategory = useSetRecoilState(giftItemInfoSubCategoryFilterAtom);
  const setTax = useSetRecoilState(giftItemInfoTaxFilterAtom);
  const openProducts = (id: string) => {
    setBrand('all');
    setCategory(id);
    setType('Delivery');
    setSearch('');
    setStatus('all');
    setSubCategory('all');
    setTax('all');
    navigate('/gift-seller/item-info');
  };
  const counts = useMemo(
    () => ({
      all: data.length,
      enabled: data.filter((item) => item.isEnabled !== false).length,
      disabled: data.filter((item) => item.isEnabled === false).length,
    }),
    [data],
  );
  const filteredData = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    return data.filter(
      (item) =>
        (statusFilter === 'all' ||
          (statusFilter === 'enabled'
            ? item.isEnabled !== false
            : item.isEnabled === false)) &&
        (!query ||
          item.name.toLowerCase().includes(query) ||
          item.subCategories?.some((sub) => sub.toLowerCase().includes(query))),
    );
  }, [data, searchQuery, statusFilter]);
  const isFiltered = Boolean(searchQuery) || statusFilter !== 'all';
  const resetFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
  };
  const retry = () => invalidateQueriesByUrlPart(queryClient, 'gift-delivery-category');
  const handleDelete = useCallback(
    async (category: GiftDeliveryCategoryData) => {
      if (!confirm(`"${category.name}" 카테고리를 삭제하시겠습니까?`)) return;
      try {
        setDeletingId(category._id);
        await deleteGiftDeliveryCategory(category._id, sellerRole());
        setData((prev) => prev.filter((item) => item._id !== category._id));
        await invalidateQueriesByUrlPart(queryClient, 'gift-delivery-category');
        alert('삭제되었습니다.');
      } catch (error) {
        console.error(error);
        alert('삭제에 실패했습니다.');
      } finally {
        setDeletingId(null);
      }
    },
    [sellerRole, setData, queryClient],
  );

  return (
    <div
      className={`${styles.page} min-h-screen bg-gray-50 px-4 py-6 text-gray-900 sm:px-6 lg:px-8 lg:py-8`}
    >
      <div className="mx-auto max-w-[1440px]">
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">배송 카테고리</h1>
            <p className="mt-2 text-sm text-gray-500">
              카테고리별 하위분류와 연결된 원상품, 고객 화면 노출을 관리합니다.
            </p>
          </div>
          <Link to="/gift-seller/delivery-category/new" className={primaryButton}>
            <PlusIcon aria-hidden className="h-4 w-4" />
            카테고리 등록
          </Link>
        </header>
        <section
          className="rounded-md border border-gray-200 bg-white"
          aria-label="배송 카테고리 목록"
        >
          <div className="flex flex-col gap-4 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
            <div
              className="flex flex-wrap items-center gap-2"
              aria-label="사용 여부 필터"
            >
              {(
                [
                  ['all', '전체'],
                  ['enabled', '사용'],
                  ['disabled', '미사용'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={statusFilter === value}
                  onClick={() => setStatusFilter(value)}
                  className={`rounded px-3 py-2 text-sm ${
                    statusFilter === value
                      ? 'bg-gray-900 font-medium text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {label} {status === 'success' ? counts[value] : '—'}
                </button>
              ))}
              {isFiltered && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="rounded px-2 py-2 text-sm text-gray-500 hover:text-gray-900"
                >
                  필터 초기화
                </button>
              )}
            </div>
            <div className="relative w-full lg:max-w-sm">
              <SearchIcon
                aria-hidden
                className="absolute left-3 top-3 h-4 w-4 text-gray-400"
              />
              <input
                type="search"
                aria-label="카테고리 검색"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="카테고리명, 하위분류 검색"
                className="w-full rounded border border-gray-300 py-2 pl-9 pr-3 text-sm"
              />
            </div>
          </div>
          {status === 'success' && (
            <p
              role="status"
              className="border-t border-gray-100 px-5 py-3 text-xs text-gray-500"
            >
              전체 {data.length}개 중 {filteredData.length}개 표시
            </p>
          )}
          {status === 'loading' || status === 'idle' ? (
            <div role="status" className="center-box gap-3 py-16">
              <Loading />
              <span className="text-sm text-gray-500">카테고리를 불러오는 중입니다.</span>
            </div>
          ) : status === 'error' ? (
            <div role="alert" className="border-t border-gray-200 px-5 py-14 text-center">
              <p className="mb-4 text-sm text-gray-600">
                카테고리 목록을 불러오지 못했습니다.
              </p>
              <button type="button" onClick={retry} className={secondaryButton}>
                다시 시도
              </button>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="border-t border-gray-200 px-5 py-14 text-center">
              <h2 className="text-base font-semibold">
                {isFiltered
                  ? '검색 조건에 맞는 카테고리가 없습니다.'
                  : '등록된 배송 카테고리가 없습니다.'}
              </h2>
              <p className="mb-5 mt-2 text-sm text-gray-500">
                {isFiltered
                  ? '검색어나 사용 여부를 변경해 보세요.'
                  : '카테고리를 등록한 후 원상품에서 연결할 수 있습니다.'}
              </p>
              {isFiltered ? (
                <button type="button" onClick={resetFilters} className={secondaryButton}>
                  필터 초기화
                </button>
              ) : (
                <Link to="/gift-seller/delivery-category/new" className={primaryButton}>
                  첫 카테고리 등록
                </Link>
              )}
            </div>
          ) : (
            <table className="block w-full text-left xl:table">
              <thead className="hidden border-t border-gray-200 bg-gray-50 text-xs font-medium text-gray-500 xl:table-header-group">
                <tr>
                  {['카테고리', '하위분류', '연결 원상품', '사용 여부', '관리'].map(
                    (title) => (
                      <th key={title} scope="col" className="px-5 py-3">
                        {title}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="block xl:table-row-group">
                {filteredData.map((item) => {
                  const count = Array.isArray(item.giftItemInfos)
                    ? item.giftItemInfos.length
                    : null;
                  const editUrl = `/gift-seller/delivery-category/${item._id}/edit`;
                  return (
                    <tr
                      key={item._id}
                      className="block border-t border-gray-200 bg-white py-4 hover:bg-gray-50/60 xl:table-row xl:py-0"
                    >
                      <th scope="row" className={`${cell} xl:w-1/4`}>
                        <div className="flex items-center gap-3">
                          {item.image?.url ? (
                            <img
                              src={fileBucketUrl(item.image.url, 'raw')}
                              alt=""
                              className="h-12 w-12 shrink-0 rounded border border-gray-100 object-cover"
                            />
                          ) : (
                            <div
                              aria-hidden
                              className="flex h-12 w-12 shrink-0 items-center justify-center rounded border border-gray-200 bg-gray-50 text-lg text-gray-400"
                            >
                              {item.name.charAt(0)}
                            </div>
                          )}
                          <div className="min-w-0">
                            <Link
                              to={editUrl}
                              className="break-words text-base font-semibold text-gray-900 hover:text-indigo-700 hover:underline"
                            >
                              {item.name}
                            </Link>
                            <p className="mt-1 text-xs font-normal text-gray-500">
                              표시 순서 {item.order ?? '미설정'}
                            </p>
                          </div>
                        </div>
                      </th>
                      <td className={`${cell} xl:w-1/3`}>
                        <span className="mb-1 block text-xs text-gray-500 xl:hidden">
                          하위분류
                        </span>
                        {item.subCategories?.length ? (
                          <div className="flex flex-wrap gap-1.5">
                            {item.subCategories.map((sub) => (
                              <span
                                key={sub}
                                className="break-words rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-600"
                              >
                                {sub}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">하위분류 없음</span>
                        )}
                      </td>
                      <td className={cell}>
                        <button
                          type="button"
                          onClick={() => openProducts(item._id)}
                          aria-label={`${item.name} 연결 원상품 ${
                            count === null ? '관리' : `${count}개 보기`
                          }`}
                          className="rounded py-1 text-left text-sm text-gray-700 hover:text-indigo-700 hover:underline"
                        >
                          <span className="mr-2 text-xs text-gray-500 xl:hidden">
                            연결 원상품
                          </span>
                          {count === null
                            ? '연결 상품 관리'
                            : count === 0
                            ? '0개 · 상품 연결'
                            : `${count}개`}{' '}
                          <span aria-hidden>↗</span>
                        </button>
                      </td>
                      <td className={cell}>
                        <span
                          className={`inline-flex rounded border px-2 py-1 text-xs ${
                            item.isEnabled === false
                              ? 'border-gray-200 bg-gray-50 text-gray-500'
                              : 'border-green-200 bg-green-50 text-green-700'
                          }`}
                        >
                          {item.isEnabled === false ? '미사용' : '사용'}
                        </span>
                      </td>
                      <td className={cell}>
                        <Menu as="div" className="relative inline-block">
                          <Menu.Button
                            aria-label={`${item.name} 더보기`}
                            disabled={deletingId === item._id}
                            className="rounded p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-40"
                          >
                            <DotsHorizontalIcon aria-hidden className="h-5 w-5" />
                          </Menu.Button>
                          <Menu.Items className="absolute left-0 z-20 mt-1 w-36 rounded-md border border-gray-200 bg-white p-1 shadow-lg focus:outline-none xl:left-auto xl:right-0">
                            <Menu.Item>
                              {({ active }) => (
                                <Link
                                  to={editUrl}
                                  className={`block rounded px-3 py-2 text-sm text-gray-700 ${
                                    active ? 'bg-gray-100' : ''
                                  }`}
                                >
                                  카테고리 수정
                                </Link>
                              )}
                            </Menu.Item>
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  type="button"
                                  onClick={() => handleDelete(item)}
                                  disabled={deletingId === item._id}
                                  className={`w-full rounded px-3 py-2 text-left text-sm text-red-700 ${
                                    active ? 'bg-red-50' : ''
                                  }`}
                                >
                                  카테고리 삭제
                                </button>
                              )}
                            </Menu.Item>
                          </Menu.Items>
                        </Menu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </div>
  );
};
